import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import {
  Container,
  FormikWrapper,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  oneTimeLogin,
  resetPassword,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_SUCCESS,
} from '@uncinc/react-drupal-core';

import { Button, Main, PasswordField } from '../../../components';
import { MINIMUM_PASSWORD_CHARACTER_COUNT } from '../../../config/constants';
import Page from '../../../containers/Page';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';

import ResetPasswordSuccess from './Success';
import InvalidPasswordResetToken from './InvalidToken';

import './style.scss';

const ResetPassword = ({ uid, timestamp, hash }) => {
  const dispatch = useDispatch();
  const validOneTimeLogin = useSelector((state) => state.getIn(['login', 'validOneTimeLogin'], null));
  const passResetToken = useSelector((state) => state.getIn(['login', 'passResetToken'], null));
  const requestStatus = useOperationStatus('user', 'reset_password');
  const { buildClass } = useClasses('ResetPassword');
  const { t } = useTranslation();

  // Validate login tokens.
  useEffect(() => {
    dispatch(oneTimeLogin(uid, timestamp, hash));
  }, [dispatch, uid, timestamp, hash]);

  const onSubmit = (password) => {
    dispatch(resetPassword(password, passResetToken));
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(MINIMUM_PASSWORD_CHARACTER_COUNT, t('pages.reset_password.fields.password.validation.min', { count: MINIMUM_PASSWORD_CHARACTER_COUNT }))
      .matches(/^(?=.*\d)(?=.*[a-zA-Z]).*$/, t('pages.reset_password.fields.password.validation.invalid'))
      .required(t('pages.reset_password.fields.password.validation.required')),
    password_confirm: Yup.string()
      .oneOf([Yup.ref('password'), null], t('pages.reset_password.fields.password_confirm.validation.no_match'))
      .required(t('pages.reset_password.fields.password.validation.required')),
  });

  const returnComponent = () => {
    if (requestStatus === UI_OPERATION_STATUS_ERROR || !validOneTimeLogin) {
      return <InvalidPasswordResetToken />;
    }

    if (requestStatus === UI_OPERATION_STATUS_SUCCESS) {
      return <ResetPasswordSuccess />;
    }

    return (
      <>
        <div className={buildClass('title')}>
          <h1>{t('pages.reset_password.title')}</h1>
        </div>

        <FormikWrapper
          initialValues={{ password: '', password_confirm: '' }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            onSubmit(values.password);
            setSubmitting(false);
          }}
          validateOnBlur={false}
          validateOnChange={false}
        >
          <PasswordField
            fieldName="password"
            title={t('pages.reset_password.fields.password.label')}
          />
          <PasswordField
            fieldName="password_confirm"
            title={t('pages.reset_password.fields.password_confirm.label')}
          />
          <Button
            htmlType="submit"
            className={buildClass('submit')}
            buttonState={requestStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
          >
            {t('buttons.submit')}
          </Button>
        </FormikWrapper>
      </>
    );
  };

  return (
    <Page className={buildClass()} hideMenuMain hideChrome title={t('pages.reset_password.title')}>
      <Container>
        <div className="row">
          <Main className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 py-8 py-lg-12">
            {returnComponent()}
          </Main>
        </div>
      </Container>
    </Page>
  );
};

export default ResetPassword;
